class FlooringCollectionSort {
  static async mapLimited(items, limit, callback) {
    const results = new Array(items.length);
    let next = 0;
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (next < items.length) {
        const index = next++;
        results[index] = await callback(items[index], index);
      }
    }));
    return results;
  }

  static indexUrl(source, page) {
    const url = new URL(source);
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    url.searchParams.set('view', 'flooring-sort-index');
    url.searchParams.set('sort_by', 'title-ascending');
    url.searchParams.set('page', String(page));
    return url;
  }

  static async readIndex(source, signal, request = fetch) {
    const readPage = async page => {
      const response = await request(this.indexUrl(source, page), { signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Flooring price index unavailable');
      const data = await response.json();
      if (!Number.isInteger(data.total) || data.total < 0 || data.total > 25000 ||
          !Number.isInteger(data.pages) || data.pages < 0 || data.pages > 100 ||
          data.page !== page || !Array.isArray(data.products)) {
        throw new Error('Invalid flooring price index');
      }
      return data;
    };
    const first = await readPage(1);
    const remaining = await this.mapLimited(
      Array.from({ length: Math.max(0, first.pages - 1) }, (_, index) => index + 2),
      3,
      readPage
    );
    if (remaining.some(page => page.total !== first.total || page.pages !== first.pages)) {
      throw new Error('Collection changed during sorting');
    }
    const products = [first, ...remaining].flatMap(page => page.products);
    const ids = new Set();
    const handles = new Set();
    for (const product of products) {
      if (typeof product.id !== 'string' || !/^\d+$/.test(product.id) ||
          typeof product.handle !== 'string' || !/^[a-z0-9][a-z0-9-]*$/.test(product.handle) ||
          !Number.isSafeInteger(product.price) || product.price < 0 || typeof product.sqm !== 'boolean' ||
          ids.has(product.id) || handles.has(product.handle)) {
        throw new Error('Invalid or duplicate flooring price entry');
      }
      ids.add(product.id);
      handles.add(product.handle);
    }
    if (products.length !== first.total) throw new Error('Incomplete flooring price index');
    return products;
  }

  static ordered(products, direction) {
    const factor = direction === 'price-descending' ? -1 : 1;
    return products.slice().sort((left, right) =>
      (left.price - right.price) * factor || left.handle.localeCompare(right.handle)
    );
  }

  constructor(grid, config, source) {
    this.grid = grid;
    this.config = config;
    this.source = source;
    this.pageSize = Math.min(250, Math.max(1, Number(config.pageSize) || 30));
    this.offset = (Math.max(1, Number.parseInt(source.searchParams.get('page'), 10) || 1) - 1) * this.pageSize;
    this.loaded = 0;
    this.controller = new AbortController();
    this.cards = new Map();
    this.busy = false;
    this.status = document.createElement('p');
    this.status.className = 'flooring-sort-status';
    this.status.setAttribute('role', 'status');
    this.status.setAttribute('aria-live', 'polite');
    grid.before(this.status);
  }

  valid() {
    return !this.controller.signal.aborted && this.grid.isConnected &&
      location.pathname + location.search === this.source.pathname + this.source.search;
  }

  stop() {
    this.controller.abort();
    this.status.remove();
    if (this.pagination?.isConnected) {
      if (this.originalPagination) this.pagination.replaceWith(this.originalPagination);
      else this.pagination.remove();
    }
    this.grid.removeAttribute('aria-busy');
    delete this.grid.dataset.flooringSortBusy;
    delete this.grid.dataset.flooringSortActive;
  }

  async requestCards(products) {
    const missing = products.filter(product => !this.cards.has(product.handle));
    const batches = [];
    for (let index = 0; index < missing.length; index += 20) batches.push(missing.slice(index, index + 20));
    await FlooringCollectionSort.mapLimited(batches, 3, async batch => {
      const url = new URL(this.config.searchUrl, location.origin);
      url.searchParams.set('view', 'flooring-sort-cards');
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', batch.map(product => product.handle).join('|'));
      for (const key of ['preview_theme_id', '_fd', 'pb']) {
        if (this.source.searchParams.has(key)) url.searchParams.set(key, this.source.searchParams.get(key));
      }
      const response = await fetch(url, { credentials: 'same-origin', signal: this.controller.signal });
      if (!response.ok) throw new Error('Sorted product cards unavailable');
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (parsed.querySelector('.shopify-section') || !parsed.querySelector('[data-flooring-sort-cards]')) {
        throw new Error('Sorted product card template unavailable');
      }
      const nodes = [...parsed.querySelectorAll('[data-flooring-sort-cards] > li[data-flooring-sort-handle]')];
      for (const product of batch) {
        const card = nodes.find(node => node.dataset.flooringSortHandle === product.handle);
        if (!card || card.querySelector('.product-item')?.dataset.productId !== product.id) {
          throw new Error('Missing sorted product card');
        }
        this.cards.set(product.handle, card);
      }
    });
    return products.map(product => this.cards.get(product.handle));
  }

  createPagination() {
    this.originalPagination = this.grid.parentElement.querySelector('.pagination-wrapper');
    this.pagination = document.createElement('div');
    this.pagination.className = 'flooring-sort-pagination';
    this.count = document.createElement('p');
    const progress = document.createElement('div');
    progress.className = 'pagination-total-progress';
    this.progress = document.createElement('span');
    this.progress.className = 'pagination-total-item';
    progress.append(this.progress);
    this.more = document.createElement('button');
    this.more.type = 'button';
    this.more.className = 'button button--secondary';
    this.more.addEventListener('click', () => this.showNext());
    this.pagination.append(this.count, progress, this.more);
    if (this.originalPagination) this.originalPagination.replaceWith(this.pagination);
    else this.grid.after(this.pagination);
  }

  updatePagination() {
    const end = this.offset + this.loaded;
    this.count.textContent = `${this.config.startCount} ${this.offset + 1} - ${end}${this.config.endCount.replace('__COUNT__', this.products.length)}`;
    this.progress.style.width = `${Math.min(100, end / this.products.length * 100)}%`;
    this.more.textContent = end < this.products.length ? this.config.showMore : this.config.noMore;
    this.more.disabled = end >= this.products.length;
  }

  async showNext() {
    if (this.busy || !this.valid()) return;
    this.busy = true;
    this.more.disabled = true;
    this.more.setAttribute('aria-busy', 'true');
    try {
      const next = this.products.slice(this.offset + this.loaded, this.offset + this.loaded + this.pageSize);
      const cards = await this.requestCards(next);
      if (!this.valid()) return;
      this.grid.append(...cards);
      this.loaded += cards.length;
      this.status.textContent = '';
    } catch (error) {
      if (error.name !== 'AbortError' && this.valid()) {
        this.status.textContent = this.config.error;
        console.warn('Flooring collection sorting:', error);
      }
    } finally {
      this.busy = false;
      if (this.valid()) {
        this.updatePagination();
        this.more.removeAttribute('aria-busy');
      }
    }
  }

  async start() {
    this.grid.dataset.flooringSortBusy = 'true';
    this.grid.setAttribute('aria-busy', 'true');
    this.status.textContent = '';
    this.status.dataset.loading = 'true';
    this.status.setAttribute('aria-label', this.config.loading);
    try {
      const products = await FlooringCollectionSort.readIndex(this.source, this.controller.signal);
      if (!this.valid()) return;
      if (!products.length || products.some(product => !product.sqm)) {
        this.status.textContent = this.config.error;
        return;
      }
      this.products = FlooringCollectionSort.ordered(products, this.source.searchParams.get('sort_by'));
      this.offset = Math.min(this.offset, Math.floor((products.length - 1) / this.pageSize) * this.pageSize);
      const visible = this.products.slice(this.offset, this.offset + this.pageSize);
      const cards = await this.requestCards(visible);
      if (!this.valid()) return;
      this.grid.replaceChildren(...cards);
      this.loaded = cards.length;
      this.createPagination();
      this.updatePagination();
      this.status.textContent = '';
      this.grid.dataset.flooringSortActive = this.source.searchParams.get('sort_by');
    } catch (error) {
      if (error.name !== 'AbortError' && this.valid()) {
        this.status.textContent = this.config.error;
        console.warn('Flooring collection sorting:', error);
      }
    } finally {
      delete this.status.dataset.loading;
      this.status.removeAttribute('aria-label');
      if (this.grid.isConnected) {
        this.grid.removeAttribute('aria-busy');
        delete this.grid.dataset.flooringSortBusy;
      }
    }
  }
}

(() => {
  let active;
  let lastGrid;
  let lastUrl;
  let pending = false;
  const scan = () => {
    pending = false;
    const grid = document.querySelector('#main-collection-product-grid.productListing');
    const source = new URL(location.href);
    if (grid === lastGrid && source.href === lastUrl) return;
    active?.stop();
    active = null;
    lastGrid = grid;
    lastUrl = source.href;
    if (!grid || !['price-ascending', 'price-descending'].includes(source.searchParams.get('sort_by')) ||
        !grid.querySelector('.price--flooring-collection')) return;
    const settings = document.getElementById('FlooringCollectionSortConfig');
    if (!settings) return;
    try {
      active = new FlooringCollectionSort(grid, JSON.parse(settings.textContent), source);
      active.start();
    } catch (error) {
      console.warn('Flooring collection sorting:', error);
    }
  };
  const schedule = () => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(scan);
  };
  window.addEventListener('popstate', schedule);
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('ptOnLoad', schedule);
  const container = document.getElementById('CollectionProductGrid');
  if (container) new MutationObserver(schedule).observe(container, { childList: true, subtree: true });
  scan();
})();